import {useEffect, useMemo, useState} from 'react';
import {functionalitiesAliasMap, strings} from '../../util/strings';
import {BuStyle, Campaign, SelectedBu, UseBannersResult} from './types';
import moment from 'moment';
import {get} from '../../api/api';
import {uriGetCampaigns} from '../../api/endpoints';
import {MenuItem} from '../app-nav-menu/types';

export function useAppNavMenuItems(
  labels: typeof strings,
  tenant: any,
): MenuItem[] {
  const [items, setItems] = useState<MenuItem[]>([]);

  const allLabels = {...strings.pt.menu, ...labels.pt.menu};

  useEffect(() => {
    if (!tenant) return;
    const arrayItems: MenuItem[] = (
      eval(tenant?.portalMenuStructure) || []
    ).map((item: any) => {
      const transformed: any = {
        label: (allLabels as any)[item.menuLabelAlias],
        funct: (functionalitiesAliasMap as any)[item.menuFeatureAlias],
        selector: [],
        url: item.url,
      };

      if (item.subItems) {
        transformed.subItems = item.subItems.map((sub: any) => ({
          label: (allLabels as any)[sub.menuLabelAlias],
          funct: (functionalitiesAliasMap as any)[sub.menuFeatureAlias],
          selector: (functionalitiesAliasMap as any)[sub.menuFeatureAlias],
          url: sub.url,
        }));
      }

      return transformed;
    });

    // Add selector fallback logic
    arrayItems?.forEach((item) => {
      if (!item.selector.length) {
        let urls: string[] = [];
        if (item.url) {
          urls.push(item.url);
        } else if (item.subItems) {
          urls = item.subItems.map((s) => s.url);
        }
        item.selector = urls;
      }
    });

    setItems(arrayItems);
  }, [tenant, labels]);

  return items;
}

export function useBanners(selectedBu: {buId: string}): UseBannersResult {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [hideBanner, setHideBanner] = useState(false);
  const [bannersEmpty, setBannersEmpty] = useState(false);

  const featureName = 'Campaigns';

  useEffect(() => {
    const getBanners = async () => {
      if (window.location.pathname !== '/inicio') return;

      const currentDate = moment();

      try {
        const response = (await get(
          uriGetCampaigns(selectedBu.buId),
          false,
          0,
          featureName,
        )) as {
          data: {items: Campaign[]};
        };
        const items = response.data.items || [];

        const active = items.filter((r: Campaign) => r.status === 'ACTIVE');
        const visible = active.filter((campaign: Campaign) => {
          const inactive = campaign.campaignInactiveBUS.includes(
            selectedBu.buId,
          );
          const starts = moment(
            campaign.startDate,
            'DD-MM-YYYY',
          ).isSameOrBefore(currentDate, 'day');
          const ends = moment(campaign.endDate, 'DD-MM-YYYY').isSameOrAfter(
            currentDate,
            'day',
          );
          return !inactive && starts && ends;
        });

        items.sort(
          (a: {endDate: string}, b: {endDate: string}) =>
            Number(a.endDate) - Number(b.endDate),
        );

        setCampaigns(active.slice(0, 5));
        setHideBanner(items.length === 0 || visible.length === 0);
        setBannersEmpty(false);
      } catch {
        setBannersEmpty(true);
      }
    };

    getBanners();
  }, [selectedBu.buId, window.location.pathname]);

  return {campaigns, hideBanner, bannersEmpty};
}

export function useAppWrapperState(
  selectedBu: SelectedBu,
  busStyles: BuStyle[],
  setUrl: (url: string) => void,
): {updateImages: number; inHomePage: boolean} {
  const [updateImages, setUpdateImages] = useState(0);
  const [inHomePage, setInHomePage] = useState(false);

  useEffect(() => {
    setInHomePage(window.location.pathname === '/inicio');
  }, [window.location.pathname]);

  useEffect(() => {
    try {
      const match = busStyles?.find((el) => el.buId === selectedBu.buId);
      const logoutUrl = match?.urlLogout;
      if (logoutUrl) {
        setUrl(logoutUrl);
      }
    } catch (err) {
      console.log('Error setting logout URL:', err);
    }
  }, [busStyles, selectedBu.buId, setUrl]);

  useMemo(() => {
    if (selectedBu.buId) {
      setUpdateImages((prev) => prev + 1);
    }
  }, [selectedBu.buId]);

  return {updateImages, inHomePage};
}
